import { AdZone } from 'adcontrolx-lib'
import { useContext, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { PRODUCT_MAP } from '../../../constants/products'
import * as SETTINGS from '../../../constants/settings'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import useIsMobileBrowser from '../../../hooks/useIsMobileBrowser'
import { appendStoredQueryParams } from '../../../utils/utilities'
import Countdown from '../Countdown'
import TextWrapper from '../TextWrapper'
import AdBannerDesktop from './AdBannerDesktop'
import './AdBanners.css'

const toastOptions = {
  position: 'top-center',
  hideProgressBar: true,
  draggable: false,
  closeButton: true,
  className: '!bg-transparent'
}

const AdBanners = () => {
  const {
    productsService,
    [SETTINGS.BANNER_DATA.key]: bannerData,
    kevelIds
  } = useContext(SiteConfigContext)
  const bannerDataRef = useRef(bannerData)
  const { getSubType } = useContext(AuthenticationContext)
  const subType = getSubType({ anyOpenHouse: true })
  const { details } = productsService[PRODUCT_MAP.THIS_PRODUCT]

  const isMobile = useIsMobileBrowser()

  const toastRef = useRef(null)
  const kevelAdContentRef = useRef(null)
  const [kevelAdIsReady, setKevelAdIsReady] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const dismissToast = () => {
    if (toastRef.current) {
      toast.dismiss(toastRef.current)
      toastRef.current = null
    }
  }

  const handleFlagForDismissal = () => {
    setIsDismissed(true)
  }

  const handleDismiss = () => {
    setIsDismissed(true)
    dismissToast()
  }

  useEffect(() => {
    // Cleanup function to dismiss toast when component unmounts
    return () => dismissToast()
  }, [])

  useEffect(() => {
    if (!isMobile && toastRef.current) {
      dismissToast()
    }
  }, [isMobile])

  // Toast logic
  useEffect(() => {
    if (JSON.stringify(bannerDataRef.current) !== JSON.stringify(bannerData)) {
      bannerDataRef.current = bannerData
      setIsDismissed(false)
      dismissToast()
    }

    if (
      !isMobile || // Don't create unless device is mobile.
      toastRef.current || // Don't create if one exists already.
      isDismissed || // Don't create if previously dismissed.
      !bannerData || // Don't create if banner data is missing
      bannerData.hideOnMobile // Don't create if set to hide on mobile.
    ) {
      return
    }

    if (
      bannerData.useKevelAdZone &&
      kevelIds &&
      kevelAdContentRef.current &&
      kevelAdIsReady
    ) {
      const kevelToastId = toast(
        <div className="relative">
          <div className="adcontrolx_header_zone w-screen p-1">
            {kevelAdContentRef.current}
          </div>
        </div>,
        {
          ...toastOptions,
          autoClose: bannerData.autoCloseTime,
          onClose: (reason) => {
            if (reason) handleDismiss()
          }
        }
      )
      toastRef.current = kevelToastId
    } else if (!bannerData.useKevelAdZone) {
      const toastId = createToast(
        bannerData,
        handleDismiss,
        handleFlagForDismissal
      )
      toastRef.current = toastId
    }
  }, [isMobile, isDismissed, bannerData, kevelAdIsReady, kevelIds])

  if (isMobile || isDismissed || !bannerData) return null

  if (
    bannerData.openHouseOnly &&
    details?.openHouse?.enabled &&
    details?.openHouse?.itemNumber !== subType
  ) {
    return null
  }

  if (isMobile && bannerData.useKevelAdZone) {
    return bannerData.useKevelAdZone ? (
      <div className="flex">
        <div className="mx-auto w-full p-0">
          <AdZone
            zoneId={kevelIds.zoneIds.banner.id}
            adType={kevelIds.zoneIds.banner.type}
          >
            {(_, ad) => {
              if (ad) {
                kevelAdContentRef.current = ad
                setTimeout(() => {
                  setKevelAdIsReady(true)
                }, 0)
              }
            }}
          </AdZone>
        </div>
      </div>
    ) : null
  }

  return <AdBannerDesktop handleDismiss={handleDismiss} />
}

const createToast = (bannerData, handleDismiss, handleFlagForDismissal) => {
  const bgColor = bannerData.bgColor
    ? `bg-${bannerData.bgColor}-400 border-${bannerData.bgColor}-500`
    : 'bg-yellow-400 border-yellow-500'
  const buttonColor = bannerData.buttonColor
    ? `bg-${bannerData.buttonColor}-500 border-${bannerData.buttonColor}-600`
    : 'bg-green-500 border-green-600'

  return toast(
    <div
      className={`AdBanners ${bgColor} flex flex-col p-2 w-full shadow-md border rounded-lg`}
    >
      {bannerData.showCountdown && (
        <div className="mx-auto">
          <span className="flex items-center text-center font-bold italic text-xs gap-1">
            {bannerData.countdownText}
            <Countdown
              endDate={bannerData.endDateTime}
              endEvent={handleDismiss}
            />
          </span>
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center w-full gap-1 px-2 cursor-pointer"
        onClick={() => {
          const linkWParams = appendStoredQueryParams(bannerData.promoLink)
          window.open(linkWParams, '_blank')
          handleDismiss()
        }}
      >
        <div className="text-black w-fit text-center [&_p]:!text-base">
          <TextWrapper renderLinks={true} text={bannerData.body} />
        </div>
        {bannerData.showButton && (
          <button
            className={`${buttonColor} border rounded px-2 py-1 text-sm font-bold whitespace-nowrap transition-all duration-300 text-black`}
          >
            {bannerData.buttonText}
          </button>
        )}
      </div>
    </div>,
    {
      ...toastOptions,
      autoClose: bannerData.autoCloseTime,
      onClose: (reason) => {
        if (reason) handleFlagForDismissal()
      }
    }
  )
}

export default AdBanners
